// 🔧 become_sponsor.js
// ✅ Purpose: Public self-service sponsor signup and payment page
// ✅ Features:
// - Loads sponsor levels from event settings
// - Shows only levels with paymentRequired and amountCents
// - Lets sponsor enter business/contact details
// - Creates pending sponsor + Stripe Checkout session
// - Redirects sponsor to Stripe Checkout
// - Uses event-level Stripe Connect payments

import { isEventPublic } from "../../utils/event_visibility";
import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import SeoHead from "../../components/seo_head";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

const BecomeSponsor = () => {

  const {
    eventslug,
    eventId,
    eventDoc,
    ready,
    eventLoading,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const [levels, setLevels] = useState([]);
  const [selectedLevelName, setSelectedLevelName] = useState("");
  const [form, setForm] = useState({
    sponsorName: "",
    sponsorContactEmail: "",
    url: "",
    description: "",
  });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadSponsorLevels = async () => {
      if (!eventId) return;

      try {
        setLoading(true);

        const settingsSnap = await getDoc(
          doc(db, "events", eventId, "settings", "voting")
        );

        const settings = settingsSnap.exists() ? settingsSnap.data() : {};

        const paidLevels = (settings.sponsorLevels || []).filter(
          (level) =>
            level.paymentRequired === true &&
            Number(level.amountCents || 0) > 0
        );

        setLevels(paidLevels);

        if (paidLevels.length > 0) {
          setSelectedLevelName(paidLevels[0].name);
        }
      } catch (err) {
        logger.error("❌ Failed to load public sponsor levels:", {
          value: err,
        });

        setStatus({
          type: "error",
          message: "Unable to load sponsorship options.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadSponsorLevels();
  }, [eventId]);

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  // =====================================================
  // 🎗 Sponsors Add-on Page Gate
  // =====================================================
  // ✅ Purpose:
  // - Prevent direct URL access to sponsor pages when the Sponsors add-on is inactive
  // - Keeps public, sponsor-contact, and manager sponsor tools hidden
  // - Friendly message instead of exposing sponsor functionality
  // =====================================================
  const sponsorsEnabled =
    hasPurchasedAddOn(eventDoc, "sponsors") ||
    hasPurchasedAddOn(eventDoc, "sponsor");

  if (!sponsorsEnabled) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "900px" }}>
          <div className="alert alert-info text-center mb-0">
            Sponsors are not active for this event.
          </div>
        </div>
      </section>
    );
  }

  const selectedLevel = levels.find(
    (level) => level.name === selectedLevelName
  );

  const formatMoney = (amountCents = 0) => {
    return `$${(Number(amountCents || 0) / 100).toFixed(2)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!eventId || !selectedLevel) return;

    if (!form.sponsorName.trim() || !form.sponsorContactEmail.trim()) {
      setStatus({
        type: "error",
        message: "Sponsor name and contact email are required.",
      });

      return;
    }

    try {
      setSaving(true);

      const createPublicSponsorCheckoutSession = httpsCallable(
        functions,
        "createPublicSponsorCheckoutSession"
      );

      const result = await createPublicSponsorCheckoutSession({
        eventId,
        sponsorName: form.sponsorName,
        sponsorContactEmail: form.sponsorContactEmail,
        url: form.url,
        description: form.description,
        sponsorLevelName: selectedLevel.name,
      });

      const checkoutUrl = result?.data?.url;

      if (!checkoutUrl) {
        throw new Error("Checkout URL was not returned.");
      }

      // 📊 Google Analytics
      trackEvent({
        action: GA_EVENTS.SPONSOR_CHECKOUT_STARTED,
        parameters: {
          event_slug: eventslug,
          event_id: eventId,
          commerce_type: "sponsor",
          source_page: window.location.pathname,
        },
      });

      window.location.href = checkoutUrl;
    } catch (err) {
      logger.error("❌ Public sponsor checkout failed:", {
        value: err,
      });

      setStatus({
        type: "error",
        message:
          err?.message ||
          "Unable to start sponsorship checkout. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const becomeSponsorSeo = {
    title: `Become a Sponsor | ${eventDoc?.name || "Event"} | Let's Go Vote`,
    description: `Support ${
      eventDoc?.name || "this event"
    } by becoming an event sponsor through Let's Go Vote.`,
    path: `/${eventslug}/become_sponsor`,
    image:
      eventDoc?.eventLogo ||
      "https://letsgovote.app/images/social-share-sponsor-management.png",
    noindex: !isEventPublic(eventDoc),
    breadcrumbs: [
      { name: "Home", path: "/" },
      {
        name: eventDoc?.name || "Event",
        path: `/${eventslug}/event`,
      },
      {
        name: "Sponsors",
        path: `/${eventslug}/sponsors`,
      },
      {
        name: "Become a Sponsor",
        path: `/${eventslug}/become_sponsor`,
      },
    ],
  };

  if (!ready || eventLoading || loading) {
    return <FullPageSpinner message="Loading sponsorship options..." />;
  }

  return (
    <>
    <SeoHead {...becomeSponsorSeo} />
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "900px" }}>
        <div className="row mb-4">
          <div className="col text-start">
            <h1 className="display-5">🎗 Become a Sponsor</h1>
            <p className="lead text-muted mb-0">
              Support {eventDoc?.name || "this event"} by choosing a sponsorship level.
            </p>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : "alert-danger"
            }`}
          >
            {status.message}
          </div>
        )}

        {levels.length === 0 ? (
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="alert alert-info mb-0">
                Online sponsorships are not available for this event yet.
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white">
                <strong>Choose Sponsorship Level</strong>
              </div>

              <div className="card-body">
                <div className="row g-3">
                  {levels.map((level) => (
                    <div key={level.name} className="col-12 col-md-6">
                      <div
                        className={`border rounded p-3 h-100 ${
                          selectedLevelName === level.name
                            ? "border-primary bg-light"
                            : "bg-white"
                        }`}
                      >
                        <div className="form-check">
                          <input
                            type="radio"
                            className="form-check-input"
                            id={`level-${level.name}`}
                            checked={selectedLevelName === level.name}
                            onChange={() => setSelectedLevelName(level.name)}
                          />

                          <label
                            className="form-check-label fw-bold"
                            htmlFor={`level-${level.name}`}
                          >
                            {level.name}
                          </label>
                        </div>

                        <div className="fs-4 fw-bold mt-2">
                          {formatMoney(level.amountCents)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white">
                <strong>Sponsor Information</strong>
              </div>

              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-bold">Sponsor Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="sponsorName"
                    value={form.sponsorName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Contact Email *</label>
                  <input
                    type="email"
                    className="form-control"
                    name="sponsorContactEmail"
                    value={form.sponsorContactEmail}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Website URL</label>
                  <input
                    type="url"
                    className="form-control"
                    name="url"
                    value={form.url}
                    onChange={handleChange}
                    placeholder="https://example.com"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Short Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    rows="4"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-success btn-lg"
              disabled={saving}
            >
              {saving
                ? "Starting Checkout..."
                : `Continue to Payment ${
                    selectedLevel ? formatMoney(selectedLevel.amountCents) : ""
                  }`}
            </button>
          </form>
        )}
      </div>
    </section>
    </>
  );
};

export default BecomeSponsor;
